import { differenceInDays } from 'date-fns';
import { z } from 'zod';
export const emailBasicFilterSchema = z.object({
    customerId: z.coerce.number().nullable().default(null),
    recipient: z.string().email('This is not a valid email.').or(z.literal('')),
    dateRangeFrom: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    dateRangeTo: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
});
export const historicalRecordsBasicFilterSchema = z
    .preprocess((data, ctx) => {
    const parsedData = emailBasicFilterSchema
        .pick({ recipient: true, customerId: true })
        .safeParse(data);
    if (parsedData.success) {
        if (!parsedData.data.recipient && !parsedData.data.customerId) {
            ctx.addIssue({
                message: '',
                path: ['recipient'],
                code: z.ZodIssueCode.custom,
            });
            ctx.addIssue({
                message: '',
                path: ['customerId'],
                code: z.ZodIssueCode.custom,
            });
        }
    }
    return data;
}, emailBasicFilterSchema)
    .refine((data) => {
    if (!data.dateRangeFrom || !data.dateRangeTo)
        return false;
    const from = new Date(data.dateRangeFrom);
    const to = new Date(data.dateRangeTo);
    return differenceInDays(to, from) <= 29;
}, {
    message: 'Date range must be within 30 days',
    path: ['dateRangeTo'],
});
export const advancedBasicFilterSchema = z.object({
    sender: z
        .string()
        .email('This is not a valid email.')
        .optional()
        .or(z.literal('')),
    isSent: z.boolean().nullable().default(null),
    event: z.string().nullable().default(''),
    officeId: z.coerce.number().nullable().default(null),
    template: z.coerce.string().nullable().default(null),
    dateRangeFrom: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    dateRangeTo: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
});
export const historicalRecordsAdvancedFilterSchema = advancedBasicFilterSchema
    .refine((data) => {
    if (!data.dateRangeFrom || !data.dateRangeTo)
        return false;
    const from = new Date(data.dateRangeFrom);
    const to = new Date(data.dateRangeTo);
    return differenceInDays(to, from) <= 6;
}, {
    message: 'Date range must be within 7 days',
    path: ['dateRangeTo'],
})
    .superRefine((data, ctx) => {
    if (!data.sender &&
        data.isSent === null &&
        data.officeId === null &&
        data.template === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to SendGrid, Office, or Template must be provided',
            path: ['sender'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to SendGrid, Office, or Template must be provided',
            path: ['isSent'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to SendGrid, Office, or Template must be provided',
            path: ['officeId'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to SendGrid, Office, or Template must be provided',
            path: ['template'],
        });
    }
});
export const EmailTemplateSchema = z.object({
    name: z.string().min(1, 'This field is required'),
    content: z.string().min(1, 'This field is required'),
    subject: z.string().min(1, 'This field is required'),
    notification_types: z
        .array(z.object({
        label: z.string(),
        value: z.string(),
    }))
        .nonempty('This field is required'),
    is_tcpa: z.boolean(),
});
export const SMSTemplateSchema = z.object({
    name: z.string().min(1, 'This field is required'),
    content: z.string().min(1, 'This field is required'),
    description: z.string().min(1, 'This field is required'),
    // TODO: when COMS-424 is done, uncomment the following line
    // notification_types: z
    //   .array(
    //     z.object({
    //       label: z.string(),
    //       value: z.string(),
    //     }),
    //   )
    //   .nonempty('This field is required'),
    notification_types: z.string().min(1, 'This field is required'),
    active: z.boolean(),
    is_tcpa: z.boolean(),
});
const smsBaseSchema = z.object({
    dateRangeFrom: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    dateRangeTo: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    customerId: z.coerce.number().nullable().default(null),
    officeId: z.coerce.number().nullable().default(null),
    event: z.string().nullable().default(''),
    type: z.string().nullable().default(''),
    isSent: z.boolean().nullable().default(null),
    isTcpa: z.boolean().nullable().default(null),
    recipient: z.string().nullable().default(''),
    sender: z.string().nullable().default(''),
});
export const smsHistoricalRecordSchema = z
    .preprocess((data, ctx) => {
    const parsedData = smsBaseSchema
        .pick({ recipient: true, customerId: true })
        .safeParse(data);
    if (parsedData.success) {
        if (!parsedData.data.recipient && !parsedData.data.customerId) {
            ctx.addIssue({
                message: '',
                path: ['recipient'],
                code: z.ZodIssueCode.custom,
            });
            ctx.addIssue({
                message: '',
                path: ['customerId'],
                code: z.ZodIssueCode.custom,
            });
        }
    }
    return data;
}, smsBaseSchema)
    .refine((data) => {
    if (!data.dateRangeFrom || !data.dateRangeTo)
        return false;
    const from = new Date(data.dateRangeFrom);
    const to = new Date(data.dateRangeTo);
    return differenceInDays(to, from) <= 6;
}, {
    message: 'Date range must be within 7 days',
    path: ['dateRangeTo'],
});
export const smsBasicFilterSchema = z.object({
    customerId: z.coerce.number().nullable().default(null),
    recipient: z.string().default(''),
    dateRangeFrom: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    dateRangeTo: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
});
export const smsHistoricalRecordsBasicFilterSchema = z
    .preprocess((data, ctx) => {
    const parsedData = smsBasicFilterSchema
        .pick({ recipient: true, customerId: true })
        .safeParse(data);
    if (parsedData.success) {
        if (!parsedData.data.recipient && !parsedData.data.customerId) {
            ctx.addIssue({
                message: '',
                path: ['recipient'],
                code: z.ZodIssueCode.custom,
            });
            ctx.addIssue({
                message: '',
                path: ['customerId'],
                code: z.ZodIssueCode.custom,
            });
        }
    }
    return data;
}, smsBasicFilterSchema)
    .refine((data) => {
    if (!data.dateRangeFrom || !data.dateRangeTo)
        return false;
    const from = new Date(data.dateRangeFrom);
    const to = new Date(data.dateRangeTo);
    return differenceInDays(to, from) <= 29;
}, {
    message: 'Date range must be within 30 days',
    path: ['dateRangeTo'],
});
export const smsAdvancedFilterSchema = z.object({
    sender: z.string().optional().or(z.literal('')),
    isSent: z.boolean().nullable().default(null),
    isTcpa: z.boolean().nullable().default(null),
    event: z.string().nullable().default(''),
    type: z.string().nullable().default(''),
    officeId: z.coerce.number().nullable().default(null),
    dateRangeFrom: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
    dateRangeTo: z.union([z.string(), z.date()]).refine((val) => !!val, {
        message: 'This field is required',
    }),
});
export const smsHistoricalRecordsAdvancedFilterSchema = smsAdvancedFilterSchema
    .refine((data) => {
    if (!data.dateRangeFrom || !data.dateRangeTo)
        return false;
    const from = new Date(data.dateRangeFrom);
    const to = new Date(data.dateRangeTo);
    return differenceInDays(to, from) <= 6;
}, {
    message: 'Date range must be within 7 days',
    path: ['dateRangeTo'],
})
    .superRefine((data, ctx) => {
    if (!data.sender &&
        data.isSent === null &&
        data.isTcpa === null &&
        data.officeId === null &&
        data.event === null &&
        data.type === null) {
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['sender'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['isSent'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['isTcpa'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['officeId'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['event'],
        });
        ctx.addIssue({
            code: 'custom',
            message: 'At least one of Sender, Delivered to Twilio, TCPA, Twilio Event, Office or SMS Type must be provided',
            path: ['type'],
        });
    }
});
